import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function isPdf(filename) {
  return !!filename && filename.toLowerCase().endsWith('.pdf');
}

function formatDateTime(v) {
  if (!v) return '—';
  return new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function EvaluateForm({ submission, maxMarks, onSubmit, submitting }) {
  const [marks, setMarks] = useState(submission.marks ?? '');
  const [comment, setComment] = useState(submission.comment || '');

  return (
    <div className="mt-12 p-16" style={{ background: '#fff8f0', borderRadius: 12, border: '1px solid #ffe6cc' }}>
      <div className="fw-semibold mb-8">Grade Submission</div>
      <div className="row g-12">
        <div className="col-sm-3">
          <label className="text-13 text-gray-500 mb-4 d-block">Marks{maxMarks !== null ? ` (max ${maxMarks})` : ''}</label>
          <input type="number" min="0" max={maxMarks ?? undefined} step="0.5" className="form-control py-9" value={marks} onChange={(e) => setMarks(e.target.value)} />
        </div>
        <div className="col-sm-9">
          <label className="text-13 text-gray-500 mb-4 d-block">Comment (visible to student)</label>
          <input type="text" className="form-control py-9" value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
      </div>
      <div className="d-flex justify-content-end mt-12">
        <button type="button" className="btn btn-sm btn-main rounded-pill py-6 px-16" disabled={submitting}
          onClick={() => onSubmit(submission.submission_id, marks, comment)}>
          {submitting ? 'Saving...' : submission.reviewed_at ? 'Re-grade' : 'Award Marks'}
        </button>
      </div>
    </div>
  );
}

function AdminAssignmentSubmissions() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [evaluatingId, setEvaluatingId] = useState(null);

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) { setError('Authentication required.'); setLoading(false); return; }
      const response = await axios.get(`${apiUrl}/api/admin/assignments/${id}/submissions`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        setAssignment(response.data.assignment);
        setSubmissions(response.data.submissions || []);
      } else {
        setError(response.data.message || 'Failed to fetch submissions.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching submissions.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleView = (s) => {
    if (isPdf(s.original_filename)) {
      navigate('/admin-assignment-submission-pdf', {
        state: {
          submissionId: s.submission_id,
          filename: s.original_filename,
          assignmentTitle: assignment?.title,
          studentName: `${s.firstName} ${s.lastName}`,
        },
      });
    } else {
      handleDownload(s.submission_id, s.original_filename);
    }
  };

  const handleDownload = async (submissionId, filename) => {
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/assignments/submissions/${submissionId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'submission');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Error downloading file.');
    }
  };

  const handleEvaluate = async (submissionId, marks, comment) => {
    setError('');
    setSuccess('');
    if (marks === '' || isNaN(Number(marks)) || Number(marks) < 0) {
      setError('Please enter a valid mark.');
      return;
    }
    setEvaluatingId(submissionId);
    try {
      const token = getToken();
      const response = await axios.put(
        `${apiUrl}/api/admin/assignments/submissions/${submissionId}/evaluate`,
        { marks, comment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setSuccess('Submission graded.');
        fetchSubmissions();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(response.data.message || 'Failed to grade submission.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error grading submission.');
    } finally {
      setEvaluatingId(null);
    }
  };

  if (loading) {
    return (
      <>
        <SideBar />
        <div className="dashboard-main-wrapper">
          <TopNavBar />
          <div className="dashboard-body text-center py-40"><div className="spinner-border text-main-600" /></div>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><Link to="/admin-assignments" className="text-gray-200 fw-normal text-15 hover-text-main-600">Assignments</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Submissions</span></li>
              </ul>
            </div>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          {assignment && (
            <div className="card mb-24">
              <div className="card-body">
                <div className="fw-bold text-16 mb-4">{assignment.title}</div>
                <div className="text-13 text-gray-500">
                  Deadline: {assignment.deadline ? new Date(assignment.deadline).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'No deadline'}
                  {assignment.max_marks !== null && ` · Max marks: ${assignment.max_marks}`}
                </div>
              </div>
            </div>
          )}

          {submissions.length === 0 ? (
            <div className="card"><div className="card-body text-center py-40 text-gray-400">No eligible students found for this assignment.</div></div>
          ) : (
            submissions.map((s) => (
              <div className="card mb-16" key={s.student_id}>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-8">
                    <div>
                      <div className="fw-semibold">{s.firstName} {s.lastName} <span className="text-13 text-gray-400">({s.studentid})</span></div>
                      {s.submitted_at ? (
                        <div className="text-13 text-gray-500 mt-4">
                          Submitted {formatDateTime(s.submitted_at)} —{' '}
                          <span
                            role="button"
                            className="text-main-600 text-decoration-underline"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleView(s)}
                          >
                            {isPdf(s.original_filename) ? 'View' : 'Download'} {s.original_filename || 'file'}
                          </span>
                          {isPdf(s.original_filename) && (
                            <>
                              {' · '}
                              <span
                                role="button"
                                className="text-main-600 text-decoration-underline"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleDownload(s.submission_id, s.original_filename)}
                              >
                                Download
                              </span>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="text-13 text-gray-400 mt-4">Not submitted yet</div>
                      )}
                    </div>
                    <div className="text-end">
                      {s.submitted_at && (
                        s.reviewed_at ? (
                          <span className="badge bg-success-subtle text-success-emphasis">
                            {s.marks}{assignment?.max_marks !== null ? ` / ${assignment.max_marks}` : ''}
                          </span>
                        ) : (
                          <span className="badge bg-warning-subtle text-warning-emphasis">Review Pending</span>
                        )
                      )}
                    </div>
                  </div>

                  {s.comment && (
                    <div className="text-13 text-gray-500 mt-8">Comment: {s.comment}</div>
                  )}

                  {s.submitted_at && (
                    <EvaluateForm
                      submission={s}
                      maxMarks={assignment?.max_marks ?? null}
                      onSubmit={handleEvaluate}
                      submitting={evaluatingId === s.submission_id}
                    />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminAssignmentSubmissions;
